// Step 4 — Review & submit.
//
// Summarises every choice the consumer made, gives them an "Edit" jump-link
// back to each step, and ships the whole lot to `/api/public/wizard-submit`
// via `./submit.ts`. On success the consumer is redirected to the portal URL
// returned by the API. On failure we show an inline banner with retry +
// "call us on <phone>" fallback.

import type { WizardState } from "../types";
import type { StepContext } from "./index";
import { escape, escapeAttr } from "./index";
import { renderRoomSvg } from "./canvas";
import { submitWizard } from "./submit";
import { flush as flushAnalytics, track } from "./analytics";
import { loadTurnstile, TURNSTILE_SITE_KEY } from "./turnstile";

// Module-scoped so a visitor going back to step 3 and returning to step 4
// doesn't stack up a fresh Turnstile widget/iframe behind the previous one.
let turnstileWidgetId: string | null = null;

/** Contact-detail glyphs — same 24×24 / 1.5px line set as the rest of the
 *  wizard, sized down inline to sit on a text baseline. */
function contactIcon(paths: string): string {
  return `<svg class="wiz-contact-icon" width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
    aria-hidden="true">${paths}</svg>`;
}

const CONTACT_ICONS = {
  mail: contactIcon(`<rect x="2.5" y="5" width="19" height="14" rx="2"/><path d="m3 6.5 9 6 9-6"/>`),
  phone: contactIcon(`<path d="M6.5 3h3l1.5 4-2 1.5a12 12 0 0 0 5.5 5.5L16 12l4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4 6.2 2 2 0 0 1 6 4z"/>`),
  pin: contactIcon(`<path d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11z"/><circle cx="12" cy="10" r="2.5"/>`),
};

export function mountStep4(host: HTMLElement, ctx: StepContext): void {
  host.innerHTML = `
    <div class="wiz-card">
      <h2 class="wiz-card__title">${escape(ctx.state.branding.reviewHeadline)}</h2>
      <p class="wiz-card__subtitle">${escape(ctx.state.branding.reviewSubhead)}</p>
      <div data-role="sections"></div>
    </div>

    <div class="wiz-banner wiz-banner--info">
      <strong>What happens next?</strong>
      ${escape(ctx.state.branding.whatHappensNext)}
    </div>

    <div data-role="status"></div>
    <div data-role="turnstile"></div>

    <div class="wiz-footer">
      <button class="wiz-btn wiz-btn--secondary" data-action="back">← Back</button>
      <button class="wiz-btn" data-action="submit">Submit my design ✓</button>
    </div>
  `;

  renderSections(host.querySelector<HTMLElement>("[data-role=sections]")!, ctx);

  host.querySelector<HTMLButtonElement>('[data-action=back]')!
    .addEventListener("click", () => ctx.goTo(2));

  const submitBtn = host.querySelector<HTMLButtonElement>('[data-action=submit]')!;
  const statusHost = host.querySelector<HTMLElement>('[data-role=status]')!;

  // 2026-08-03: a real-traffic test found the button was clickable the
  // instant step 4 rendered — well before loadTurnstile() (fresh script
  // fetch from a third-party origin) + widget render + invisible solve could
  // complete, which can take 1-3s. That race sent an empty turnstileToken to
  // the server every time it fired. Gate the button on token-readiness so an
  // empty-token submit is no longer possible from timing alone.
  let turnstileToken = "";
  const setSubmitReady = (ready: boolean): void => {
    submitBtn.disabled = !ready;
    submitBtn.textContent = ready ? "Submit my design ✓" : "Verifying…";
  };
  setSubmitReady(false);

  const turnstileHost = host.querySelector<HTMLElement>("[data-role=turnstile]")!;
  loadTurnstile()
    .then((ts) => {
      if (turnstileWidgetId) {
        try { ts.remove(turnstileWidgetId); } catch { /* widget already gone */ }
      }
      turnstileWidgetId = ts.render(turnstileHost, {
        sitekey: TURNSTILE_SITE_KEY,
        appearance: "interaction-only",
        callback: (token: string) => {
          turnstileToken = token;
          console.log("[wizard] Turnstile solved, token length", token.length);
          setSubmitReady(true);
        },
        // Previously just cleared the token with no way to get a fresh one,
        // so an expired/errored widget stayed dead until the whole step
        // remounted. reset() re-triggers execution to fetch a new token;
        // setSubmitReady(false) blocks submission in the meantime.
        "expired-callback": () => {
          console.warn("[wizard] Turnstile token expired — requesting a fresh one");
          turnstileToken = "";
          setSubmitReady(false);
          if (turnstileWidgetId) ts.reset(turnstileWidgetId);
        },
        "error-callback": (code?: string) => {
          console.warn("[wizard] Turnstile error-callback", code);
          turnstileToken = "";
          setSubmitReady(false);
          if (turnstileWidgetId) ts.reset(turnstileWidgetId);
        },
      });
    })
    .catch((e) => {
      // Cloudflare CDN hiccup — don't strand a real customer on step 4. The
      // server verifies independently and will surface its own error if the
      // token really is missing/invalid.
      console.error("[wizard] Turnstile failed to load:", e);
      setSubmitReady(true);
    });

  submitBtn.addEventListener("click", () => {
    void doSubmit(host, ctx, submitBtn, statusHost, () => turnstileToken, setSubmitReady);
  });
}

async function doSubmit(
  host: HTMLElement,
  ctx: StepContext,
  submitBtn: HTMLButtonElement,
  statusHost: HTMLElement,
  getTurnstileToken: () => string,
  setSubmitReady: (ready: boolean) => void,
): Promise<void> {
  submitBtn.disabled = true;
  submitBtn.innerHTML = `<span class="wiz-spinner" style="margin-right:0.5rem;"></span> Submitting…`;
  statusHost.innerHTML = "";

  const result = await submitWizard(ctx.state, getTurnstileToken());

  if (result.ok) {
    // Mark the funnel completed and flush before anything navigates away.
    track({
      eventType: "wizard_submitted",
      ...(ctx.state.style.doorRangeId ? { rangeId: ctx.state.style.doorRangeId } : {}),
      ...(ctx.state.style.doorRange   ? { rangeName: ctx.state.style.doorRange } : {}),
      ...(ctx.state.style.colourName  ? { colourName: ctx.state.style.colourName } : {}),
    });
    flushAnalytics();
    // "Check your email" popup — the server has emailed their portal login
    // details. The button opens the login page; it no longer signs them in on
    // the way (magic link replaced by the temp-password email, 2026-08-12).
    showCheckEmailPopup(ctx.state.customer.email, result.portalUrl);
    return;
  }

  // Failure — re-enable (unless the token expired mid-flight, in which case
  // stay disabled/"Verifying…" until the widget's own reset() lands a fresh
  // one and fires setSubmitReady(true) again) — then show error + fallback.
  setSubmitReady(getTurnstileToken().length > 0);
  const phone = ctx.state.branding.phone;
  statusHost.innerHTML = `
    <div class="wiz-banner wiz-banner--error">
      <strong>Something went wrong.</strong>
      ${escape(result.error)}
      <div style="margin-top:0.5rem;">
        <button class="wiz-btn wiz-btn--secondary" data-role="retry">Try again</button>
        ${phone ? `or call us on <a href="tel:${escapeAttr(phone)}"><strong>${escape(phone)}</strong></a>.` : ""}
      </div>
    </div>`;
  statusHost.querySelector<HTMLButtonElement>("[data-role=retry]")?.addEventListener("click", () => {
    void doSubmit(host, ctx, submitBtn, statusHost, getTurnstileToken, setSubmitReady);
  });
}

// ── "Check your email" success popup ────────────────────────────────────────
// Shown after a successful submit. Confirms we've emailed a portal link and
// lets the customer continue straight to their portal. Custom inline SVG icon
// (1.5px stroke, rounded) per the design rules — no emoji.
function showCheckEmailPopup(email: string, portalUrl: string): void {
  const go = (): void => {
    if (!portalUrl) return;
    try { window.top!.location.href = portalUrl; }
    catch { window.location.href = portalUrl; }
  };
  const overlay = document.createElement("div");
  overlay.className = "wiz-modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Check your email");
  overlay.innerHTML = `
    <div class="wiz-modal">
      <div class="wiz-modal__icon" aria-hidden="true">
        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="5" width="18" height="14" rx="2.5"/>
          <path d="m3.5 7 8.5 5.5L20.5 7"/>
        </svg>
      </div>
      <h3 class="wiz-modal__title">Check your email</h3>
      <p class="wiz-modal__body">
        Your design is on its way. We've sent your login details${email ? ` to <strong>${escape(email)}</strong>` : ""}
        so you can open your kitchen project portal any time — your designs, quotes and
        messages, all in one place.
      </p>
      ${portalUrl ? `<button class="wiz-btn wiz-btn--primary wiz-modal__btn" data-role="go-portal">Go to my portal</button>` : ""}
    </div>`;
  document.body.appendChild(overlay);
  overlay.querySelector<HTMLButtonElement>("[data-role=go-portal]")?.addEventListener("click", go);
}

// ── Section summaries ───────────────────────────────────────────────────────

function renderSections(host: HTMLElement, ctx: StepContext): void {
  host.innerHTML = `
    ${section("Your room", roomPlanHtml(ctx.state) + summariseRoom(ctx.state), 0, ctx)}
    ${section("Style & colour", summariseStyle(ctx.state), 1, ctx)}
    ${section("Your details", summariseDetails(ctx.state), 2, ctx)}
    ${section("Booking", summariseBooking(ctx.state), 2, ctx)}
  `;
  host.querySelectorAll<HTMLButtonElement>('[data-jump]').forEach((b) => {
    b.addEventListener("click", () => ctx.goTo(Number(b.dataset.jump)));
  });
}

function section(title: string, bodyHtml: string, stepIdx: number, _ctx: StepContext): string {
  return `
    <div class="wiz-review-section">
      <div class="wiz-review-section__body">
        <h4 class="wiz-review-section__title">${escape(title)}</h4>
        ${bodyHtml}
      </div>
      <button class="wiz-review-section__edit" data-jump="${stepIdx}">Edit</button>
    </div>
  `;
}

// Visual floor plan for the review screen (Bug 12). Returns "" when nothing
// was drawn, so the text summary's "No walls drawn yet" still shows.
function roomPlanHtml(s: WizardState): string {
  const svg = renderRoomSvg(s.room);
  return svg ? `<div class="wiz-review-plan">${svg}</div>` : "";
}

function summariseRoom(s: WizardState): string {
  const r = s.room;
  if (r.walls.length === 0) {
    return `<em>No walls drawn yet — that's OK, you can sketch later.</em>`;
  }
  const parts = [
    `${r.walls.length} wall${r.walls.length === 1 ? "" : "s"}`,
    `${r.doors.length} door${r.doors.length === 1 ? "" : "s"}`,
    `${r.windows.length} window${r.windows.length === 1 ? "" : "s"}`,
  ];
  if (r.boilers.length > 0) parts.push(`${r.boilers.length} boiler`);
  parts.push(`ceiling ${r.ceilingHeightMm} mm`);
  return parts.join(" • ");
}

function summariseStyle(s: WizardState): string {
  const a = s.style.appliances;
  const rows: Array<string> = [];
  if (s.style.doorRange) {
    rows.push(`<strong>Door range:</strong> ${escape(s.style.doorRange)}${
      s.style.colourName ? ` in <em>${escape(s.style.colourName)}</em>` : ""
    }`);
  }
  if (s.style.worktop)   rows.push(`<strong>Worktop:</strong> ${escape(s.style.worktop)}`);
  if (s.style.storage.length > 0)
    rows.push(`<strong>Storage:</strong> ${s.style.storage.map(escape).join(", ")}`);

  const appBits: string[] = [];
  for (const [k, v] of Object.entries(a)) {
    if (k === "lighting") continue;
    if (Array.isArray(v) && v.length > 0) appBits.push(`${capitalise(k)}: ${v.map(escape).join(", ")}`);
  }
  if (a.lighting.length > 0) appBits.push(`Lighting: ${a.lighting.map(escape).join(", ")}`);
  if (appBits.length > 0)  rows.push(`<strong>Appliances:</strong> ${appBits.join(" • ")}`);

  if (rows.length === 0) return `<em>No selections yet.</em>`;
  return rows.join("<br />");
}

function summariseDetails(s: WizardState): string {
  const c = s.customer;
  const p = s.project;
  const rows: string[] = [];
  rows.push(`<strong>${escape((c.firstName + " " + c.lastName).trim() || "(name not set)")}</strong>`);
  // Emoji replaced 2026-08-10: 📧/📞/📍 render as a different typeface (and,
  // on some platforms, full colour) next to the rest of the wizard's line
  // icons. These are drawn to the same 24×24 / 1.5px spec as every other glyph
  // in the flow.
  if (c.email) rows.push(`${CONTACT_ICONS.mail}${escape(c.email)}`);
  if (c.phone) rows.push(`${CONTACT_ICONS.phone}${escape(c.phone)}`);
  if (c.address) rows.push(`${CONTACT_ICONS.pin}${escape(c.address)}`);
  const meta: string[] = [];
  if (p.budget)     meta.push(`Budget: ${escape(p.budget)}`);
  if (p.timeline)   meta.push(`Timeline: ${escape(p.timeline)}`);
  if (p.leadSource) meta.push(`Source: ${escape(p.leadSource)}`);
  if (meta.length > 0) rows.push(meta.join(" • "));
  if (p.photos.length > 0) rows.push(`${p.photos.length} photo${p.photos.length === 1 ? "" : "s"} attached`);
  return rows.join("<br />");
}

function summariseBooking(s: WizardState): string {
  const b = s.booking;
  if (!b.bookingType) return `<em>No appointment requested — your specialist will reach out to arrange.</em>`;
  const labels: Record<string, string> = {
    showroom: "Showroom visit",
    "home-visit": "Home design visit",
    // remote / supply-only are no longer offered but still appear on
    // historical leads, so they keep their labels here.
    remote: "Remote / video consultation",
    "supply-only": "Supply only",
    callback: "Request a call",
  };
  const bits: string[] = [labels[b.bookingType] || b.bookingType];
  if (b.preferredDate) bits.push(formatDate(b.preferredDate));
  if (b.preferredTime) bits.push(b.preferredTime);
  return bits.join(" • ");
}

function capitalise(s: string): string {
  if (!s) return s;
  return s[0]!.toUpperCase() + s.slice(1);
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso + "T00:00:00");
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(d);
  } catch {
    return iso;
  }
}
